import {CheckCircle, CheckCircleOutline} from '@mui/icons-material';
import {Box, IconButton, Tooltip} from '@mui/material';
import {FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {businessesApi} from 'src/api';
import AMPModal from 'src/components/common/AMPModal';
import {EditBusinessForm} from 'src/components/forms/EditBusinessForm';
import {EditIcon} from 'src/components/icons/EditIcon';
import {useCan} from 'src/permissions/hooks';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {setActiveBusiness} from 'src/stores/common/commonSlice';
import theme from 'src/theme';
import {TENANT_SCOPED_APIS} from 'src/components/common/ActiveBusinessSelect';

/**
 * What can be done to a business from its row.
 *
 * Making it active is the same act as choosing it in the sidebar, and does the same two things -
 * remembers it and drops every tenant-scoped cache - because a business left half-switched is how a
 * screen ends up showing one tenant's layout with another's numbers.
 *
 * Editing opens the same form the business's own page uses. The record it needs is not the one in
 * the row: the list endpoint answers with `BusinessDTO`, which carries no owner, and the form writes
 * the owner's name, phone and language. So the pencil fetches the business first and opens the form
 * on the complete record - the alternative was a form that silently blanked three fields.
 */
export const BusinessRowActions: FC<{businessId: number}> = ({businessId}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const activeBusiness = useAppSelector((state) => state.common.activeBusiness);
    const canUpdate = useCan('businesses.update');
    const [editing, setEditing] = useState(false);
    const isActive = businessId === activeBusiness;
    // Only once the pencil is pressed: fetching every row's full record to have it ready would be
    // one request per business on every page of the list.
    const {data: business} = businessesApi.useGetBusinessByIdQuery({businessId}, {skip: !editing});

    const makeActive = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (isActive) {
            return;
        }
        dispatch(setActiveBusiness(businessId));
        TENANT_SCOPED_APIS.forEach((api) => dispatch(api.util.resetApiState()));
    };

    const edit = (event: React.MouseEvent) => {
        event.stopPropagation();
        setEditing(true);
    };

    return (
        <Box sx={{display: 'flex', gap: 0.5}}>
            <Tooltip
                title={intl.formatMessage({id: isActive ? 'active' : 'setAsActive'})}
            >
                {/* A span, because a disabled button fires no events and the tooltip would never show. */}
                <span>
                    <IconButton
                        size="small"
                        disabled={isActive}
                        onClick={makeActive}
                    >
                        {isActive
                            ? <CheckCircle fontSize="small" sx={{color: theme.palette.success.main}}/>
                            : <CheckCircleOutline fontSize="small"/>}
                    </IconButton>
                </span>
            </Tooltip>
            {canUpdate && (
                <Tooltip title={intl.formatMessage({id: 'editBusiness'})}>
                    <IconButton size="small" onClick={edit}>
                        <EditIcon/>
                    </IconButton>
                </Tooltip>
            )}

            {business && (
                <AMPModal
                    handleClose={() => setEditing(false)}
                    open={editing}
                    title={intl.formatMessage({id: 'editBusiness'})}
                >
                    <EditBusinessForm
                        businessData={business}
                        handleShowModal={(status: boolean) => () => setEditing(status)}
                    />
                </AMPModal>
            )}
        </Box>
    );
};
